/**
 * Performance Monitoring Service
 * 
 * Comprehensive performance tracking for identifying slow loading times
 * and optimizing navigation flows across the application.
 */

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  route?: string;
  metadata?: Record<string, any>;
}

interface RouteTransition {
  from: string;
  to: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  apiCalls: APICallMetric[];
  renderTime?: number;
}

interface APICallMetric {
  url: string;
  method: string;
  startTime: number;
  endTime: number;
  duration: number;
  status: number;
  size?: number;
  requestContext?: {
    route: string;
    userAction: string;
    isSequential: boolean;
    retryAttempt: number;
  };
  requestDetails?: {
    bodySize: number;
    queryParams: Record<string, any>;
    hasFilters: boolean;
    hasPagination: boolean;
  };
  performanceImpact?: {
    blocksUI: boolean;
    priority: 'high' | 'medium' | 'low';
    userVisible: boolean;
  };
}

interface ComponentRenderMetric {
  componentName: string;
  renderTime: number;
  props?: Record<string, any>;
  timestamp: number;
}

interface UserContext {
  userId?: string;
  organizationId?: number;
  userAgent: string;
  screenResolution: string;
  connectionType: string;
  deviceMemory?: number;
  hardwareConcurrency?: number;
}

interface RouteContext {
  path: string;
  params: Record<string, string>;
  queryParams: Record<string, string>;
  referrer: string;
  loadType: 'initial' | 'navigation' | 'refresh';
}

interface PerformanceReport {
  webVitals: Record<string, number>;
  routeTransitions: RouteTransition[];
  apiCalls: APICallMetric[];
  componentRenders: ComponentRenderMetric[];
  resourceTimings: PerformanceResourceTiming[];
  memoryUsage?: any;
  userContext: UserContext;
  sessionId: string;
  timestamp: number;
}

class PerformanceMonitorService {
  private metrics: PerformanceMetric[] = [];
  private routeTransitions: RouteTransition[] = [];
  private apiCalls: APICallMetric[] = [];
  private componentRenders: ComponentRenderMetric[] = [];
  private currentRoute: string = '';
  private currentTransition: RouteTransition | null = null;
  private isEnabled: boolean = true;
  private observers: PerformanceObserver[] = [];
  private sessionId: string;
  private userContext: UserContext;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.userContext = this.collectUserContext();
    this.initializeWebVitals();
    this.initializeRouteTracking();
    this.initializeResourceTracking();
    this.initializeNavigationTracking();
    this.logSessionStart();
  }

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private collectUserContext(): UserContext {
    const nav = navigator as any;
    const screenInfo = typeof window !== 'undefined' && window.screen ? 
      `${window.screen.width}x${window.screen.height}` : 'unknown';
    
    return {
      userAgent: navigator.userAgent,
      screenResolution: screenInfo,
      connectionType: nav.connection?.effectiveType || 'unknown',
      deviceMemory: nav.deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency
    };
  }

  private logSessionStart() {
    console.log(`🚀 Performance monitoring session started: ${this.sessionId}`, {
      userContext: this.userContext,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Initialize Web Vitals tracking
   */
  private initializeWebVitals() {
    // Import and configure Web Vitals (v5 API uses onXXX instead of getXXX)
    import('web-vitals').then(({ onCLS, onFCP, onLCP, onTTFB, onINP }) => {
      onCLS(this.handleWebVital.bind(this));
      onFCP(this.handleWebVital.bind(this));
      onLCP(this.handleWebVital.bind(this));
      onTTFB(this.handleWebVital.bind(this));
      onINP(this.handleWebVital.bind(this)); // INP replaces FID in web-vitals v4+
    }).catch((err) => {
      console.warn('Failed to initialize Web Vitals:', err);
    });
  }

  private handleWebVital(metric: any) {
    this.addMetric({
      name: `webvital_${metric.name}`,
      value: metric.value,
      timestamp: Date.now(),
      route: this.currentRoute,
      metadata: {
        id: metric.id,
        delta: metric.delta,
        rating: metric.rating
      }
    });

    // Log significant Web Vitals issues
    if (metric.rating === 'poor') {
      console.warn(`⚠️ Poor ${metric.name}: ${metric.value}ms on route ${this.currentRoute}`);
    }
  }

  /**
   * Initialize route tracking
   */
  private initializeRouteTracking() {
    // Track navigation timing
    if ('performance' in window && 'navigation' in performance) {
      window.addEventListener('load', () => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        this.addMetric({
          name: 'initial_load_time',
          value: navigation.loadEventEnd - navigation.fetchStart,
          timestamp: Date.now(),
          metadata: {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
            firstByte: navigation.responseStart - navigation.fetchStart,
            domComplete: navigation.domComplete - navigation.fetchStart
          }
        });
      });
    }
  }

  /**
   * Initialize resource tracking
   */
  private initializeResourceTracking() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const resource = entry as PerformanceResourceTiming;
            
            // Track large resources and slow requests
            if (resource.duration > 1000 || resource.transferSize > 500000) {
              this.addMetric({
                name: 'slow_resource',
                value: resource.duration,
                timestamp: Date.now(),
                route: this.currentRoute,
                metadata: {
                  name: resource.name,
                  type: this.getResourceType(resource.name),
                  size: resource.transferSize,
                  cached: resource.transferSize === 0
                }
              });
            }
          }
        }
      });
      
      observer.observe({ entryTypes: ['resource'] });
      this.observers.push(observer);
    }
  }

  /**
   * Initialize navigation performance tracking
   */
  private initializeNavigationTracking() {
    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) { // Long task threshold
            this.addMetric({
              name: 'long_task',
              value: entry.duration,
              timestamp: Date.now(),
              route: this.currentRoute,
              metadata: {
                startTime: entry.startTime,
                name: entry.name
              }
            });
          }
        }
      });
      
      try {
        observer.observe({ entryTypes: ['longtask'] });
        this.observers.push(observer);
      } catch (e) {
        // longtask not supported in all browsers
      }
    }
  }

  /**
   * Start tracking a route transition
   */
  startRouteTransition(from: string, to: string) {
    if (!this.isEnabled) return;

    // End current transition if exists
    if (this.currentTransition) {
      this.endRouteTransition();
    }

    const startTime = performance.now();
    this.currentTransition = {
      from,
      to,
      startTime,
      apiCalls: []
    };

    // Mark the start of route transition
    performance.mark(`route-transition-start-${to}`);
    
    console.log(`🚀 Starting route transition: ${from} → ${to}`);
  }

  /**
   * End tracking a route transition
   */
  endRouteTransition() {
    if (!this.currentTransition || !this.isEnabled) return;

    const endTime = performance.now();
    const duration = endTime - this.currentTransition.startTime;
    
    this.currentTransition.endTime = endTime;
    this.currentTransition.duration = duration;

    // Mark the end of route transition
    performance.mark(`route-transition-end-${this.currentTransition.to}`);
    performance.measure(
      `route-transition-${this.currentTransition.to}`,
      `route-transition-start-${this.currentTransition.to}`,
      `route-transition-end-${this.currentTransition.to}`
    );

    this.routeTransitions.push({ ...this.currentTransition });
    this.currentRoute = this.currentTransition.to;

    // Log slow transitions
    if (duration > 2000) {
      console.warn(`🐌 Slow route transition (${duration.toFixed(2)}ms): ${this.currentTransition.from} → ${this.currentTransition.to}`);
    } else {
      console.log(`✅ Route transition completed (${duration.toFixed(2)}ms): ${this.currentTransition.from} → ${this.currentTransition.to}`);
    }

    this.addMetric({
      name: 'route_transition',
      value: duration,
      timestamp: Date.now(),
      route: this.currentTransition.to,
      metadata: {
        from: this.currentTransition.from,
        apiCallCount: this.currentTransition.apiCalls.length
      }
    });

    this.currentTransition = null;
  }

  /**
   * Track API call performance
   */
  trackAPICall(url: string, method: string, startTime: number, endTime: number, status: number, size?: number) {
    if (!this.isEnabled) return;

    const duration = endTime - startTime;
    const apiCall: APICallMetric = {
      url,
      method,
      startTime,
      endTime,
      duration,
      status,
      size
    };

    this.apiCalls.push(apiCall);

    // Add to current transition if active
    if (this.currentTransition) {
      this.currentTransition.apiCalls.push(apiCall);
    }

    // Log slow API calls
    if (duration > 5000) {
      console.warn(`🐌 Slow API call (${duration.toFixed(2)}ms): ${method} ${url}`);
    }

    this.addMetric({
      name: 'api_call',
      value: duration,
      timestamp: Date.now(),
      route: this.currentRoute,
      metadata: {
        url,
        method,
        status,
        size,
        endpoint: this.extractEndpoint(url)
      }
    });
  }

  /**
   * Track component render performance
   */
  trackComponentRender(componentName: string, renderTime: number, props?: Record<string, any>) {
    if (!this.isEnabled) return;

    const render: ComponentRenderMetric = {
      componentName,
      renderTime,
      props,
      timestamp: Date.now()
    };

    this.componentRenders.push(render);

    // Log slow renders
    if (renderTime > 100) {
      console.warn(`🐌 Slow component render (${renderTime.toFixed(2)}ms): ${componentName}`);
    }

    this.addMetric({
      name: 'component_render',
      value: renderTime,
      timestamp: Date.now(),
      route: this.currentRoute,
      metadata: {
        componentName,
        propsCount: props ? Object.keys(props).length : 0
      }
    });
  }

  /**
   * Mark a custom performance event
   */
  mark(name: string, metadata?: Record<string, any>) {
    if (!this.isEnabled) return;

    performance.mark(name);
    this.addMetric({
      name: `custom_${name}`,
      value: performance.now(),
      timestamp: Date.now(),
      route: this.currentRoute,
      metadata
    });
  }

  /**
   * Measure time between two marks
   */
  measure(name: string, startMark: string, endMark?: string) {
    if (!this.isEnabled) return;

    try {
      performance.measure(name, startMark, endMark);
      const measure = performance.getEntriesByName(name, 'measure')[0];
      
      this.addMetric({
        name: `measure_${name}`,
        value: measure.duration,
        timestamp: Date.now(),
        route: this.currentRoute,
        metadata: {
          startMark,
          endMark
        }
      });
    } catch (error) {
      console.error('Performance measure failed:', error);
    }
  }

  /**
   * Get memory usage information
   */
  getMemoryUsage() {
    if ('memory' in performance) {
      return {
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit
      };
    }
    return null;
  }

  /**
   * Generate a comprehensive performance report
   */
  generateReport(): PerformanceReport {
    const webVitals: Record<string, number> = {};
    
    // Collect Web Vitals
    this.metrics
      .filter(m => m.name.startsWith('webvital_'))
      .forEach(m => {
        const vitalName = m.name.replace('webvital_', '');
        webVitals[vitalName] = m.value;
      });

    return {
      webVitals,
      routeTransitions: [...this.routeTransitions],
      apiCalls: [...this.apiCalls],
      componentRenders: [...this.componentRenders],
      resourceTimings: performance.getEntriesByType('resource') as PerformanceResourceTiming[],
      memoryUsage: this.getMemoryUsage(),
      userContext: this.userContext,
      sessionId: this.sessionId,
      timestamp: Date.now()
    };
  }

  /**
   * Get performance summary for current session
   */
  getSummary() {
    const report = this.generateReport();
    const slowRoutes = this.routeTransitions
      .filter(t => t.duration && t.duration > 2000)
      .sort((a, b) => (b.duration || 0) - (a.duration || 0));
    
    const slowAPIs = this.apiCalls
      .filter(a => a.duration > 2000)
      .sort((a, b) => b.duration - a.duration);

    const slowComponents = this.componentRenders
      .filter(c => c.renderTime > 100)
      .sort((a, b) => b.renderTime - a.renderTime);

    return {
      overview: {
        totalRouteTransitions: this.routeTransitions.length,
        averageRouteTime: this.routeTransitions.reduce((sum, t) => sum + (t.duration || 0), 0) / this.routeTransitions.length,
        totalAPIcalls: this.apiCalls.length,
        averageAPITime: this.apiCalls.reduce((sum, a) => sum + a.duration, 0) / this.apiCalls.length,
        memoryUsage: this.getMemoryUsage()
      },
      slowRoutes: slowRoutes.slice(0, 10),
      slowAPIs: slowAPIs.slice(0, 10),
      slowComponents: slowComponents.slice(0, 10),
      webVitals: report.webVitals
    };
  }

  /**
   * Export performance data as JSON with investigation details
   */
  exportData() {
    const report = this.generateReport();
    const investigationData = this.generateInvestigationReport();
    
    const exportData = {
      ...report,
      investigation: investigationData,
      exportedAt: new Date().toISOString(),
      sessionDuration: Date.now() - parseInt(this.sessionId.split('-')[1])
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `performance-investigation-${this.sessionId}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    console.log(`📊 Performance data exported for session: ${this.sessionId}`);
  }

  /**
   * Generate detailed investigation report
   */
  generateInvestigationReport() {
    const slowRoutes = this.routeTransitions.filter(t => t.duration && t.duration > 2000);
    const slowAPIs = this.apiCalls.filter(a => a.duration > 2000);
    const slowComponents = this.componentRenders.filter(c => c.renderTime > 100);
    
    const memoryMetrics = this.getMemoryUsage();
    const currentMemoryUsage = memoryMetrics ? 
      (memoryMetrics.usedJSHeapSize / memoryMetrics.totalJSHeapSize) * 100 : 0;

    return {
      sessionOverview: {
        sessionId: this.sessionId,
        totalMetrics: this.metrics.length,
        sessionDuration: Date.now() - parseInt(this.sessionId.split('-')[1]),
        userContext: this.userContext
      },
      performanceIssues: {
        slowRoutes: slowRoutes.map(route => ({
          ...route,
          issueType: 'slow_route',
          severity: (route.duration || 0) > 5000 ? 'high' : 'medium',
          recommendation: this.getRouteOptimizationRecommendation(route)
        })),
        slowAPIs: slowAPIs.map(api => ({
          ...api,
          issueType: 'slow_api',
          severity: api.duration > 5000 ? 'high' : 'medium',
          recommendation: this.getAPIOptimizationRecommendation(api)
        })),
        slowComponents: slowComponents.map(comp => ({
          ...comp,
          issueType: 'slow_component',
          severity: comp.renderTime > 500 ? 'high' : 'medium',
          recommendation: this.getComponentOptimizationRecommendation(comp)
        })),
        memoryUsage: {
          currentUsage: currentMemoryUsage,
          severity: currentMemoryUsage > 80 ? 'high' : currentMemoryUsage > 60 ? 'medium' : 'low',
          recommendation: currentMemoryUsage > 80 ? 
            'High memory usage detected. Consider implementing component memoization and cleaning up event listeners.' :
            'Memory usage is within acceptable limits.'
        }
      },
      investigationGuide: {
        steps: [
          'Review slow routes and check for unnecessary data loading',
          'Analyze API call patterns for potential caching opportunities',
          'Examine component render frequency and prop changes',
          'Monitor memory usage patterns for potential leaks',
          'Check Web Vitals for user experience impact'
        ],
        diagnosticQueries: [
          'Which routes have the highest average load time?',
          'What API endpoints are called most frequently?',
          'Which components re-render most often?',
          'Are there any memory usage spikes?',
          'What is the correlation between route changes and API calls?'
        ]
      }
    };
  }

  private getRouteOptimizationRecommendation(route: RouteTransition): string {
    const apiCallCount = route.apiCalls.length;
    const duration = route.duration || 0;
    
    if (apiCallCount > 5) {
      return 'Consider implementing data prefetching or reducing the number of API calls on route load.';
    }
    if (duration > 5000) {
      return 'Investigate code splitting and lazy loading for this route.';
    }
    return 'Consider optimizing initial data loading and component rendering.';
  }

  private getAPIOptimizationRecommendation(api: APICallMetric): string {
    if (api.duration > 5000) {
      return 'Consider implementing caching, pagination, or server-side optimization for this endpoint.';
    }
    if (api.size && api.size > 1000000) {
      return 'Large response size detected. Consider implementing data compression or pagination.';
    }
    return 'Monitor this API call frequency and consider caching if called repeatedly.';
  }

  private getComponentOptimizationRecommendation(comp: ComponentRenderMetric): string {
    if (comp.renderTime > 500) {
      return 'Consider implementing React.memo, useMemo, or useCallback to optimize this component.';
    }
    return 'Monitor prop changes and state updates that might cause unnecessary re-renders.';
  }

  /**
   * Clear all collected data
   */
  clear() {
    this.metrics = [];
    this.routeTransitions = [];
    this.apiCalls = [];
    this.componentRenders = [];
    performance.clearMarks();
    performance.clearMeasures();
  }

  /**
   * Enable/disable monitoring
   */
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    console.log(`Performance monitoring ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Helper methods
   */
  private addMetric(metric: PerformanceMetric) {
    this.metrics.push(metric);
    
    // Keep only last 1000 metrics to prevent memory leaks
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }
  }

  private getResourceType(url: string): string {
    if (url.includes('.js')) return 'script';
    if (url.includes('.css')) return 'stylesheet';
    if (url.match(/\.(png|jpg|jpeg|gif|svg|webp)/)) return 'image';
    if (url.includes('/api/')) return 'api';
    return 'other';
  }

  private extractEndpoint(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      return url;
    }
  }

  /**
   * Cleanup observers
   */
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitorService();

// Global access for debugging
if (typeof window !== 'undefined') {
  (window as any).performanceMonitor = performanceMonitor;
}